/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { env } from '$env/dynamic/public';
import { get } from 'svelte/store';
import type { Writable } from 'svelte/store';
import { floraVerseSession } from '$lib/stores/floraVerseStore';
import { chatSession } from '$lib/stores/chatSessionStore';
export const BRANDS: Record<string, string> = {
  '1800flowers': '18F',
  'thepopcornfactory': 'TPF',
  'fruitbouquets': 'FB',
  '1800baskets': '18B',
  'cheryls': 'CCO',
  'harryanddavid': 'HD',
  'simplychocolate': 'SCH',
  'berries': 'BRY',
  'wolfermans': 'WLF',
  'vitalchoice': 'VC',
  'florists': 'FL',
  'plants': 'PLA',
};

const getBrandAbbreviation = (fullBrandName: string): string => {
  return BRANDS[fullBrandName.toLowerCase()] || '18F';
};
declare global {
  interface Utag {
    view: (data: Record<string, unknown>) => void;
    link: (data: Record<string, unknown>) => void;
  }
  interface Window {
    utag_data?: Record<string, unknown>;
    utag?: Utag;
  }
}
const getSessionFromStore = <T>(storeSession: Writable<T>): T => {
  return get(storeSession);
};
class Tealium {
  private readonly account: string | undefined;
  private readonly profile: string | undefined;
  private readonly environment: string | undefined;
  private readonly maxRetries = 3;
  private readonly retryDelay = 500;

  constructor() {
    this.account = env.PUBLIC_APP_TIQ_ACCOUNT;
    this.profile = env.PUBLIC_APP_TIQ_PROFILE;
    this.environment = env.PUBLIC_APP_TIQ_ENV;
  }

  private loadScript() {
    // TODO: come back and get rid of this when we re-enable tealium
    const trueVar = 'true';
    if (trueVar) return;
    try {
      if (typeof window !== 'undefined' && !window.utag) {
        if (!this.account || !this.profile || !this.environment) {
          console.error('Tealium setup: Missing required parameters.');
          return;
        }
        const src = `https://tags.tiqcdn.com/utag/${this.account}/${this.profile}/${this.environment}/utag.js`;
        const script = document.createElement('script');
        script.async = true;
        script.type = 'text/javascript';
        script.src = src;

        document.head.appendChild(script);

        // Initialize data layer once the script is loaded
        script.onload = () => {
          console.log('Tealium initialized!');
          window.utag_data = {
            environment: this.environment,
          };
        };
      }
    } catch (error) {
      console.error('Error loading Tealium script:', error);
    }
  }

  public initialize() {
    // TODO: come back and get rid of this when we re-enable tealium
    const trueVar = 'true';
    if (trueVar) return;

    this.loadScript();
  }

  public trackPageView(pathName: string, pageName: string, additionalData: Record<string, unknown> = {}, retries = this.maxRetries) {
    // TODO: come back and get rid of this when we re-enable tealium
    const trueVar = 'true';
    if (trueVar) return;

    try {
      const floraVerseSessionStore = getSessionFromStore(floraVerseSession);
      const floraChatSessionStore = getSessionFromStore(chatSession)
      if (window.utag && window.utag.view) {
        window.utag.view({
          brand_current: getBrandAbbreviation(floraVerseSessionStore?.brand || floraChatSessionStore?.brand) || '',
          brand_entering: getBrandAbbreviation(floraVerseSessionStore?.brand || floraChatSessionStore?.brand) || '',
          cellid: "NO_CELL",
          country_code: "US",
          isUsCustomer: "true",
          total_search_products: 0,
          gua_title: "",
          wcs_mobile: false,
          device_type: floraChatSessionStore?.deviceType || floraVerseSessionStore?.isMobile ? "mobile" : "desktop",
          is_mobile: floraChatSessionStore?.deviceType === 'mobile' ? true : floraVerseSessionStore?.isMobile || false,
          page_type: window?.location?.pathname || floraChatSessionStore?.entry_page_type || '',
          page: pathName,
          page_name: pageName,
          page_language: 'en',
          ...additionalData,
        });
      } else if (retries > 0) {
        console.warn(`Tealium is not initialized yet. Retrying page view... (${this.maxRetries - retries + 1}/${this.maxRetries})`);
        setTimeout(() => this.trackPageView(pathName, pageName, additionalData, retries - 1), this.retryDelay);
      } else {
        console.error('Failed to track page view: Tealium is not initialized after maximum retries.');
      }
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  }

  public trackEvent(
    rawDataLayer: Record<string, unknown> = {},
    retries = this.maxRetries
  ) {
    // TODO: come back and get rid of this when we re-enable tealium
    const trueVar = 'true';
    if (trueVar) return;

    try {
      const floraVerseSessionStore = getSessionFromStore(floraVerseSession);
      const floraChatSessionStore = getSessionFromStore(chatSession)
      if (window.utag && window.utag.link) {
        window.utag.link({
          brand_current: getBrandAbbreviation(floraVerseSessionStore?.brand || floraChatSessionStore?.brand) || '',
          brand_entering: getBrandAbbreviation(floraVerseSessionStore?.brand || floraChatSessionStore?.brand) || '',
          cellid: "NO_CELL",
          country_code: "US",
          isUsCustomer: "true",
          total_search_products: 0,
          gua_title: "",
          wcs_mobile: false,
          device_type: floraChatSessionStore?.deviceType || floraVerseSessionStore?.isMobile ? "mobile" : "desktop",
          is_mobile: floraChatSessionStore?.deviceType === 'mobile' ? true : floraVerseSessionStore?.isMobile || false,
          page: window?.location?.href || floraChatSessionStore?.entry_point || '',
          page_name: window?.location?.hostname || '',
          page_type: window?.location?.pathname || floraChatSessionStore?.entry_page_type || '',
          page_language: 'en',
          event_name: rawDataLayer?.eventName || '',
          feature_category: rawDataLayer?.eventCategory || '',
          feature_detail: rawDataLayer?.eventLabel || '',
          feature_element: rawDataLayer?.eventAction || '',
          event_category: rawDataLayer?.eventCategory || '',
          event_action: rawDataLayer?.eventAction || '',
          event_label: rawDataLayer?.eventLabel || '',
          browser_uuid: floraVerseSessionStore?.browserUUID || floraChatSessionStore?.browseruuid || '',
          gua_tracker: false, // Prevent duplicate GA trackers for post-pageview events
          ...rawDataLayer
        })
      } else if (retries > 0) {
        console.warn(`Tealium is not initialized yet. Retrying event tracking... (${this.maxRetries - retries + 1}/${this.maxRetries})`);
        setTimeout(() => this.trackEvent(rawDataLayer, retries - 1), this.retryDelay);
      } else {
        console.error('Failed to track event: Tealium is not initialized after maximum retries.');
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }
}

export const tealium = new Tealium();
